import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import { Link } from 'react-router-dom';

export interface ServiceItem {
  image: string;
  tag: string;
  title: string;
  description: string;
  href: string;
}

export interface ServicesOverviewProps {
  eyebrow: string;
  headline: string;
  description: string;
  services: ServiceItem[];
  midCta?: { text: string; href: string };
}

export default function ServicesOverview({ eyebrow, headline, description, services, midCta }: ServicesOverviewProps) {
  const head = useScrollAnimation();
  const cards = useScrollAnimation();

  return (
    <section className="bg-white py-24 md:py-36 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 md:px-10">

        {/* Header */}
        <div
          ref={head.ref}
          className="flex flex-col lg:flex-row lg:items-end justify-between mb-16 md:mb-20 gap-8"
          style={{ opacity: head.visible ? 1 : 0, transform: head.visible ? 'translateY(0)' : 'translateY(24px)', transition: 'opacity 0.7s ease, transform 0.7s ease' }}
        >
          <div className="max-w-lg">
            <div className="flex items-center gap-3 mb-5">
              <span className="w-8 h-px bg-jc-gold"></span>
              <span className="text-[10px] font-bold tracking-[0.2em] uppercase text-jc-gold">{eyebrow}</span>
            </div>
            <h2 className="font-serif text-[clamp(2rem,4vw,3.2rem)] font-light text-jc-charcoal leading-[1.12]">
              {headline}
            </h2>
          </div>
          <p className="text-gray-400 text-[0.95rem] leading-relaxed max-w-sm lg:text-right">{description}</p>
        </div>

        {/* Service cards — tall, layered */}
        <div
          ref={cards.ref}
          className="grid grid-cols-1 md:grid-cols-3 gap-5"
        >
          {services.map((service, i) => (
            <Link
              key={i}
              to={service.href}
              className="group relative overflow-hidden rounded-2xl cursor-pointer block"
              style={{
                aspectRatio: i === 0 ? '3/4' : '3/4',
                opacity: cards.visible ? 1 : 0,
                transform: cards.visible ? 'translateY(0) scale(1)' : 'translateY(30px) scale(0.98)',
                transition: `opacity 0.65s ease ${i * 0.1}s, transform 0.65s ease ${i * 0.1}s`,
              }}
            >
              <img
                src={service.image}
                alt={service.title}
                className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-1000 group-hover:scale-[1.06]"
              />
              {/* Gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/15 to-transparent transition-opacity duration-500"></div>
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/15 transition-all duration-500"></div>

              {/* Tag */}
              <div className="absolute top-5 left-5 z-10">
                <span className="inline-block px-3 py-1 bg-jc-gold text-jc-charcoal text-[9px] font-bold tracking-[0.18em] uppercase rounded-full">
                  {service.tag}
                </span>
              </div>

              {/* Content */}
              <div className="absolute bottom-0 left-0 right-0 p-7 z-10">
                <h3 className="font-serif text-2xl font-light text-white leading-tight mb-2.5">
                  {service.title}
                </h3>
                <p className="text-white/60 text-sm leading-relaxed line-clamp-2 mb-4">{service.description}</p>
                <div className="flex items-center gap-2 text-jc-gold text-[10px] font-bold tracking-[0.18em] uppercase opacity-0 -translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300">
                  Learn More <i className="ri-arrow-right-line text-sm"></i>
                </div>
              </div>

              {/* Gold border on hover */}
              <div className="absolute inset-0 border border-jc-gold/0 rounded-2xl group-hover:border-jc-gold/40 transition-all duration-500 z-10"></div>
            </Link>
          ))}
        </div>

        {midCta && (
          <div className="mt-14 text-center"
            style={{ opacity: cards.visible ? 1 : 0, transition: 'opacity 0.6s ease 0.5s' }}>
            <a href={midCta.href}
              className="inline-flex items-center gap-2.5 px-9 py-4 border border-jc-charcoal text-jc-charcoal font-bold text-[10px] tracking-[0.18em] uppercase rounded-full hover:bg-jc-charcoal hover:text-white transition-all duration-300 whitespace-nowrap group">
              {midCta.text}
              <i className="ri-arrow-right-line transition-transform duration-300 group-hover:translate-x-1"></i>
            </a>
          </div>
        )}
      </div>
    </section>
  );
}
